import { ref, computed } from 'vue'
import type { Document, DocumentType, DocumentEntityType } from '@/types'

export interface MinimizedDocumentForm {
  title: string
  type: DocumentType
  content: string
  isPublic: boolean
}

export interface MinimizedDocument {
  id: string
  form: MinimizedDocumentForm
  entityType: DocumentEntityType
  entityId: string
  document: Document | null
  minimizedAt: Date
}

// --- localStorage helpers ---

const MINIMIZED_KEY = 'minimized-documents'
const DRAFT_KEY_PREFIX = 'doc-draft:'

// Stored shape — only plain serializable fields, no Firestore objects or reactive proxies
interface StoredMinimizedDocument {
  id: string
  form: MinimizedDocumentForm
  entityType: DocumentEntityType
  entityId: string
  documentId: string | null
  minimizedAt: string
}

function persistMinimized() {
  try {
    const data: StoredMinimizedDocument[] = minimizedDocuments.value.map(item => ({
      id: item.id,
      form: {
        title: item.form.title,
        type: item.form.type,
        content: item.form.content,
        isPublic: item.form.isPublic,
      },
      entityType: item.entityType,
      entityId: item.entityId,
      documentId: item.document?.id ?? null,
      minimizedAt: item.minimizedAt instanceof Date
        ? item.minimizedAt.toISOString()
        : String(item.minimizedAt),
    }))
    localStorage.setItem(MINIMIZED_KEY, JSON.stringify(data))
  } catch { /* quota exceeded or private mode — ignore */ }
}

function loadMinimized(): MinimizedDocument[] {
  try {
    const raw = localStorage.getItem(MINIMIZED_KEY)
    if (!raw) return []
    const data: StoredMinimizedDocument[] = JSON.parse(raw)
    if (!Array.isArray(data)) return []
    return data.map(item => ({
      id: item.id,
      form: item.form,
      entityType: item.entityType,
      entityId: item.entityId,
      // Reconstruct a minimal Document stub — only `id` and truthiness are used downstream
      document: item.documentId
        ? { id: item.documentId } as Document
        : null,
      minimizedAt: new Date(item.minimizedAt),
    }))
  } catch {
    return []
  }
}

// --- Draft helpers (used by DocumentFormDialog) ---

export function draftKey(entityType: string, entityId: string, documentId?: string | null): string {
  return `${DRAFT_KEY_PREFIX}${entityType}:${entityId}:${documentId || 'new'}`
}

export function saveDraft(key: string, form: MinimizedDocumentForm) {
  try {
    localStorage.setItem(key, JSON.stringify(form))
  } catch { /* ignore */ }
}

export function loadDraft(key: string): MinimizedDocumentForm | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function clearDraft(key: string) {
  try {
    localStorage.removeItem(key)
  } catch { /* ignore */ }
}

// --- Active document helpers (the one currently open in a dialog) ---

const ACTIVE_KEY = 'active-document'

interface StoredActiveDocument {
  entityType: DocumentEntityType
  entityId: string
  documentId: string | null
  form: MinimizedDocumentForm
}

function persistActive() {
  try {
    if (activeDocument.value) {
      localStorage.setItem(ACTIVE_KEY, JSON.stringify(activeDocument.value))
    } else {
      localStorage.removeItem(ACTIVE_KEY)
    }
  } catch { /* ignore */ }
}

function loadActive(): StoredActiveDocument | null {
  try {
    const raw = localStorage.getItem(ACTIVE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// --- Module-level singleton state ---

const minimizedDocuments = ref<MinimizedDocument[]>(loadMinimized())
const activeDocument = ref<StoredActiveDocument | null>(loadActive())

let idCounter = 0
function genId() {
  idCounter = (idCounter + 1) % Number.MAX_VALUE
  return `min-doc-${Date.now()}-${idCounter}`
}

export function useMinimizedDocuments() {
  function minimize(params: {
    form: MinimizedDocumentForm
    entityType: DocumentEntityType
    entityId: string
    document: Document | null
  }): string {
    const id = genId()
    minimizedDocuments.value.push({
      id,
      form: { ...params.form },
      entityType: params.entityType,
      entityId: params.entityId,
      document: params.document,
      minimizedAt: new Date(),
    })
    persistMinimized()
    return id
  }

  function restore(id: string): MinimizedDocument | undefined {
    const index = minimizedDocuments.value.findIndex((d) => d.id === id)
    if (index === -1) return undefined
    const [item] = minimizedDocuments.value.splice(index, 1)
    persistMinimized()
    return item
  }

  function discard(id: string) {
    const index = minimizedDocuments.value.findIndex((d) => d.id === id)
    if (index !== -1) {
      minimizedDocuments.value.splice(index, 1)
      persistMinimized()
    }
  }

  function findByDocumentId(documentId: string): MinimizedDocument | undefined {
    return minimizedDocuments.value.find((d) => d.document?.id === documentId)
  }

  const count = computed(() => minimizedDocuments.value.length)
  const hasMinimized = computed(() => minimizedDocuments.value.length > 0)

  function setActiveDocument(params: {
    entityType: DocumentEntityType
    entityId: string
    documentId?: string | null
    form: MinimizedDocumentForm
  }) {
    activeDocument.value = {
      entityType: params.entityType,
      entityId: params.entityId,
      documentId: params.documentId ?? null,
      form: { ...params.form },
    }
    persistActive()
  }

  function updateActiveForm(form: MinimizedDocumentForm) {
    if (!activeDocument.value) return
    activeDocument.value = { ...activeDocument.value, form: { ...form } }
    persistActive()
  }

  function clearActiveDocument() {
    activeDocument.value = null
    try { localStorage.removeItem(ACTIVE_KEY) } catch { /* ignore */ }
  }

  return {
    minimizedDocuments: computed(() => minimizedDocuments.value),
    activeDocument: computed(() => activeDocument.value),
    minimize,
    restore,
    discard,
    findByDocumentId,
    setActiveDocument,
    updateActiveForm,
    clearActiveDocument,
    count,
    hasMinimized,
  }
}
